import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import useData from "./useData";

const useRepDashboard = () => {
  const { executeAction } = useData();

  // Filter state - no repId filter for rep dashboard
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    startDate: null,
    endDate: null,
    clientId: null,
    projectId: null,
    supervisorId: null,
    repId: null,
  });

  // Data state for completion by rep
  const [completionByRep, setCompletionByRep] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  // Update filters with debounce to avoid excessive API calls
  const updateFilters = useCallback(
    debounce((newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      updateFilters.cancel();
    };
  }, [updateFilters]);

  // ===== COMPLETION BY REP =====
  const getCompletionByRep = useCallback(
    async (customFilters = null) => {
      setCompletionByRep((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "completionByRep",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setCompletionByRep((prev) => ({
            ...prev,
            data: result.data || [],
            isLoading: false,
          }));
        } else {
          setCompletionByRep((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch completion by rep",
            isLoading: false,
          }));
        }
      } catch (error) {
        setCompletionByRep((prev) => ({
          ...prev,
          error: error.message || "Error fetching completion by rep",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // Refresh metrics when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      getCompletionByRep();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    // Filters
    filters,
    updateFilters,

    // Metrics
    completionByRep,

    // Actions
    getCompletionByRep,

    // Utility: check if loading
    isLoading: completionByRep.isLoading,

    // Utility: check if error
    hasError: completionByRep.error,
  };
};

export default useRepDashboard;
